import { useChat } from '@ai-sdk/react';
import { useQuery } from '@tanstack/react-query';
import { isTextUIPart, TextStreamChatTransport, type UIMessage } from 'ai';
import { useEffect, useMemo } from 'react';

import {
  fetchSuggestionChatEndpoint,
  getFetchSuggestionChatEndpointQueryKey,
} from '@lotushealth/lotushealth_js_sdk/endpoints';

import { createLotusRequestConfig } from '@/shared/api/lotus';
import {
  createLotusAuthHeadersOptional,
  getSuggestionChatUrl,
} from '@/shared/api/lotus/hostageChat';

async function fetchHistory(
  userId: string,
  actionId: string,
  signal: AbortSignal
): Promise<UIMessage[]> {
  const config = await createLotusRequestConfig({ signal });
  const response = await fetchSuggestionChatEndpoint(userId, actionId, config);
  return response.data.messages.map(({ id, role, text }) => ({
    id,
    role,
    parts: [{ type: 'text', text }],
  }));
}

export function useSuggestionChat(userId: string, actionId: string) {
  const url = getSuggestionChatUrl(userId, actionId);
  const history = useQuery({
    queryKey: getFetchSuggestionChatEndpointQueryKey(userId, actionId),
    queryFn: ({ signal }) => fetchHistory(userId, actionId, signal),
  });
  const transport = useMemo(
    () =>
      new TextStreamChatTransport({
        api: url,
        headers: createLotusAuthHeadersOptional,
        prepareSendMessagesRequest: ({ messages }) => ({
          body: {
            message:
              messages
                .at(-1)
                ?.parts.filter(isTextUIPart)
                .map((part) => part.text)
                .join('') ?? '',
          },
        }),
      }),
    [url]
  );
  const chat = useChat({ id: `suggestion-${actionId}`, transport });
  const { setMessages } = chat;

  useEffect(() => {
    if (history.data) {
      setMessages(history.data);
    }
  }, [history.data, setMessages]);

  return {
    ...chat,
    historyError: history.data === undefined ? (history.error ?? undefined) : undefined,
    isHistoryLoading: history.isPending || history.isFetching,
    retryHistory: history.refetch,
  };
}
